import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useAuth } from '@/auth/context'
import { DEFAULT_HOME, NEXT_PARAM, safeNext } from '@/auth/routes'
import { FullPageLoading } from '@/ui/states'

/**
 * `/auth/handoff#token=…` — where a session minted on the apex becomes a
 * session on **this** host.
 *
 * The cookie is host-only and bound to one tenant (spec: *Sessions and the
 * handoff*), so signing in on `augrank.com` cannot leave a cookie for
 * `acme.augrank.com`, and switching organisation from one tenant host to
 * another cannot either. Something has to cross between them, and it is a
 * single-use token with a sixty-second life: `POST /auth/handoff` mints it on
 * the host the user is leaving, and this page redeems it on the host they are
 * arriving at.
 *
 * **The token is in the fragment, never the query string**, for the reason
 * `SupportAccess` gives: a fragment is not sent to a server, so it cannot land
 * in a `Referer`, in CloudFront's access log or in the API's own. **It is
 * cleared from the address bar before the exchange is attempted** — spent
 * either way, and not worth leaving on screen to be pasted into a ticket.
 *
 * Band 1, no guard: whoever arrives here is nobody on this host yet. That is
 * the whole purpose. `?next=` names where to go afterwards (`safeNext`, so
 * only a path on this host), else the dashboard.
 *
 * Every failure is one outcome — `/login?error=handoff` — because every reason
 * it can fail (expired, already used, minted for a different host, a token
 * that was never there) has the same remedy: sign in again on this host.
 */
export function Handoff(): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const { adopt, status } = useAuth()
  // `useRef` rather than state: React 19's StrictMode mounts an effect twice in
  // development, and the token is single-use — the second attempt would report
  // a spent token over a session that had just been established correctly.
  const attempted = useRef(false)

  useEffect(() => {
    // Not before `/auth/me` has answered. `AuthProvider` reads it on mount, and
    // a 401 landing *after* the exchange had adopted the new identity would
    // overwrite it with `anonymous` — the exchange is the child effect, so its
    // request is issued first and, on a stub or a fast API, answered first.
    if (status === 'loading') return
    if (attempted.current) return
    attempted.current = true

    const token = new URLSearchParams(location.hash.replace(/^#/, '')).get('token') ?? ''
    const next = safeNext(new URLSearchParams(location.search).get(NEXT_PARAM)) ?? DEFAULT_HOME
    // Cleared first, and unconditionally. See the module docstring.
    window.history.replaceState(null, '', `${location.pathname}${location.search}`)

    if (!token) {
      void navigate('/login?error=handoff', { replace: true })
      return
    }

    void (async () => {
      const result = await call(() =>
        api.POST('/api/v1/auth/handoff/exchange', { body: { token } }),
      )
      if (!result.ok) {
        void navigate('/login?error=handoff', { replace: true })
        return
      }
      adopt({ user: result.data.user, session: result.data.session })
      void navigate(next, { replace: true })
    })()
  }, [adopt, status, location.hash, location.pathname, location.search, navigate])

  return <FullPageLoading label="Opening your workspace" />
}
